'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  Copy01Icon,
  Tick01Icon,
} from '@terra/ui-icons';
import { MetaDefault } from '../../components/typography';
import { Textarea } from '../../components/textarea';
import { Button } from '../../components/button';
import { Clipboard } from '../../components/clipboard';
import { IconButton } from '../../components/icon-button';

type EmailRawSourceViewerProps = {
  rawPlain?: string;
  rawHtml?: string;
};

export function EmailRawSourceViewer({
  rawPlain,
  rawHtml,
}: EmailRawSourceViewerProps) {
  const t = useTranslations('designSystem.mfa.emailDisplay');
  const [isExpanded, setIsExpanded] = useState(false);
  const hasRawPlain = Boolean(rawPlain?.trim());
  const hasRawHtml = Boolean(rawHtml?.trim());

  if (!hasRawPlain && !hasRawHtml) {
    return null;
  }

  return (
    <VStack alignItems="stretch" gap={2}>
      <Button
        variant="ghost"
        size="xs"
        alignSelf="flex-start"
        onClick={() => setIsExpanded((previous) => !previous)}
        aria-label={isExpanded ? t('hideRaw') : t('viewRaw')}
      >
        <HStack gap={1} alignItems="center">
          <MetaDefault color="fg.muted">
            {isExpanded ? t('hideRaw') : t('viewRaw')}
          </MetaDefault>
          {isExpanded ? (
            <ArrowUp01Icon size={12} />
          ) : (
            <ArrowDown01Icon size={12} />
          )}
        </HStack>
      </Button>
      {isExpanded && (
        <VStack alignItems="stretch" gap={2}>
          {hasRawPlain && rawPlain && (
            <RawBlock label={t('rawPlain')} value={rawPlain} />
          )}
          {hasRawHtml && rawHtml && (
            <RawBlock label={t('rawHtml')} value={rawHtml} />
          )}
        </VStack>
      )}
    </VStack>
  );
}

function RawBlock({ label, value }: { label: string; value: string }) {
  return (
    <Box
      p={2}
      bg="bg.subtle"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.muted"
    >
      <HStack justifyContent="space-between" alignItems="center" mb={1}>
        <MetaDefault color="fg.muted">{label}</MetaDefault>
        <Clipboard.Root value={value}>
          <Clipboard.Trigger asChild>
            <IconButton variant="ghost" size="xs" aria-label={label}>
              <Clipboard.Indicator copied={<Tick01Icon size={12} />}>
                <Copy01Icon size={12} />
              </Clipboard.Indicator>
            </IconButton>
          </Clipboard.Trigger>
        </Clipboard.Root>
      </HStack>
      {/* SECURITY: rawHtml/rawPlain are untrusted. Escaped text via readonly Textarea only — never render HTML. */}
      <Textarea
        value={value}
        readOnly
        rows={6}
        fontFamily="mono"
        fontSize="xs"
        resize="vertical"
        w="full"
      />
    </Box>
  );
}

export type { EmailRawSourceViewerProps };
